import { ParseResult, PreParseResult, ValueType } from '../FormatterTypes';
import { BaseDisplayer } from '../Displayers/BaseDisplayer';
import { iLimit, stringOfChars, stringOfCharCodes } from './Other';

// Helper functions to convert ValueTypes

export function displayValue(
  value: ValueType,
  displayer?: BaseDisplayer,
  displayerParams?: string
) {
  if (displayer) {
    return displayer.display(value, true, displayerParams);
  } else {
    return asString(value) ?? '';
  }
}

export function editValue(
  value: ValueType,
  displayer?: BaseDisplayer,
  displayerParams?: string
) {
  if (displayer) {
    return displayer.display(value, false, displayerParams);
  } else {
    return asString(value) ?? '';
  }
}

/**
 * Determines if a ValueType has an acceptable value
 */
export function hasValue(value: ValueType): boolean {
  if (value === null || value === undefined) {
    return false;
  } else if (typeof value === 'string') {
    return value !== '';
  } else {
    return true;
  }
}

/**
 *
 */
export interface IFloatRec {
  exponent: number;
  negative: boolean;
  digits: number[];
}

/**
 * converts a number to an IFloatRec
 */
function numberToIFloatRec(value: number): IFloatRec {
  // Convert value to exponential form, then extracts the exponent and digits from that
  // Converts it to IFloatRec, to keep it inline with Delphi code
  const valAsStr = value.toExponential();
  const eIndex = valAsStr.search('e');

  const digits = valAsStr
    .slice(0, eIndex)
    .split('')
    .filter(d => d !== '.')
    .map(digit => digit.charCodeAt(0));

  const exponent = valAsStr.slice(eIndex + 1);

  let result = {
    exponent: parseInt(exponent, 10) + 1,
    negative: value < 0,
    digits: digits,
  };

  return result;
}

/**
 * Currency (number) to string
 */
export function currToStrEx2(
  value: number,
  minIntPart: number = 0,
  maxIntPart: number = 0,
  minDecimals: number = 2,
  maxDecimals: number = 2,
  maxPrecision: number = 0,
  thousandSep: string = '',
  decimalSep: string = ',',
  forceSign: boolean = false,
  scientific: boolean = false
): string {
  let flr: IFloatRec = numberToIFloatRec(value);

  return iFormatFloat(
    flr,
    thousandSep,
    decimalSep,
    minIntPart,
    maxIntPart,
    minDecimals,
    maxDecimals,
    maxPrecision,
    forceSign,
    scientific
  );
}

/**
 * Converts a ValueType to String
 */
export const asString = (value: ValueType): string | null => {
  if (typeof value === 'string') {
    if (!value) {
      return null;
    }
    return value;
  } else if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : floatToStrEx2(value); // det här kanske ska vara floatToStrEx??
  } else if (typeof value === 'boolean') {
    return booleanToString(value);
  }

  return null;
};

/**
 * Number to string
 */
export function floatToStrEx2(
  value: number,
  minIntPart: number = 0,
  maxIntPart: number = 0,
  minDecimals: number = 0,
  maxDecimals: number = -1,
  maxPrecision: number = 0,
  thousandSep: string = '',
  decimalSep: string = ',',
  forceSign: boolean = false,
  scientific: boolean = false
): string {
  let flr = numberToIFloatRec(value);

  let result = iFormatFloat(
    flr,
    thousandSep,
    decimalSep,
    minIntPart,
    maxIntPart,
    minDecimals,
    maxDecimals,
    maxPrecision,
    forceSign,
    scientific
  );

  return result;
}

/**
 * Converts an integer to a string (name from delphi).
 * Throws Error if supplied a floating point value
 */
export function intToStrEx2(
  value: number,
  minIntPart: number = 0,
  maxIntPart: number = 0,
  thousandSep: string = '',
  forceSign: boolean = false
): string {
  if (!Number.isInteger(value)) {
    throw new Error('Tried to supply intToStrEx2 with a double/float');
  }

  let flr = numberToIFloatRec(value);

  if (flr.negative) {
    flr.exponent--;
  }

  flr.digits.push(0);
  return iFormatFloat(
    flr,
    thousandSep,
    '',
    minIntPart,
    maxIntPart,
    0,
    0,
    0,
    forceSign,
    false
  );
}

/**
 *  Formats a number to a display value
 */
export function iFormatFloat(
  value: IFloatRec,
  thousandSep: string,
  decimalSep: string,
  minIntPart: number,
  maxIntPart: number,
  minDecimals: number,
  maxDecimals: number,
  maxPrecision: number,
  forceSign: boolean,
  scientific: boolean
): string {
  if (value.exponent === -32768 || value.exponent === 32767) {
    return '';
  }

  let prec = 0;
  let dig = 0;
  let digCnt = 0;
  let intpart = 0;
  let decpart = 0;
  let tmp = 0;
  let exppart = '';

  // Count how many digits we have
  while (
    digCnt <= 20 &&
    digCnt < value.digits.length &&
    value.digits[digCnt] !== 0
  ) {
    digCnt++;
  }

  // Determine how many digits we should have for integer/decimal part
  prec = maxPrecision > 0 ? maxPrecision : -1;
  if (scientific) {
    if (maxDecimals >= 0 && (prec <= 0 || prec > maxDecimals + 1)) {
      prec = maxDecimals + 1;
    }
  } else {
    if (prec <= 0 && maxDecimals >= 0) {
      if (value.exponent + maxDecimals >= 0) {
        prec = value.exponent + maxDecimals;
      } else {
        digCnt = 0;
      }
    }
  }

  if (prec >= 0 && digCnt > prec) {
    digCnt = prec;
    if (value.digits[digCnt] >= '5'.charCodeAt(0)) {
      while (digCnt > 0 && value.digits[digCnt - 1] >= '9'.charCodeAt(0)) {
        digCnt--;
      }
      if (digCnt === 0) {
        value.digits[0] = '1'.charCodeAt(0);
        value.exponent++;
        digCnt = 1;
      } else {
        value.digits[digCnt - 1]++;
      }
    } else if (prec === 0) {
      value.negative = false;
    }
  }

  if (maxIntPart < 1) {
    maxIntPart = Math.max(1, value.exponent, minIntPart);
  }
  minIntPart = iLimit(minIntPart, 1, maxIntPart);
  minDecimals = Math.max(minDecimals, 0);
  if (maxDecimals < 0) {
    maxDecimals = 9999;
  }

  if (scientific) {
    intpart = 1;
    decpart = iLimit(digCnt - 1, minDecimals, maxDecimals);
    if (value.exponent !== 1 && digCnt > 0) {
      exppart = `E${value.exponent - 1}`;
    } else {
      exppart = '';
    }
    dig = 0;
  } else {
    intpart = iLimit(value.exponent, minIntPart, maxIntPart);
    decpart = iLimit(digCnt - value.exponent, minDecimals, maxDecimals);
    exppart = '';
    dig = value.exponent - intpart;
  }

  // The eventual result that we build up
  let data = '';

  // Sign
  if (value.negative) {
    data += '-';
  } else if (forceSign) {
    data += '+';
  }

  // Integer part
  if (intpart > 3 && thousandSep !== '') {
    tmp = intpart % 3;
    if (tmp === 0) {
      tmp = 3;
    }
    while (intpart > 0) {
      if (tmp === 0) {
        data += thousandSep;
        tmp = 3;
      }
      tmp--;
      if (dig < 0 || dig >= digCnt) {
        data += '0';
      } else {
        data += String.fromCharCode(value.digits[dig]);
      }

      intpart--;
      dig++;
    }
  } else {
    tmp = Math.min(-dig, intpart);
    if (tmp > 0) {
      data += stringOfChars('0', tmp);
      intpart -= tmp;
      dig += tmp;
    }

    tmp = Math.min(digCnt - dig, intpart);
    if (tmp > 0) {
      data += stringOfCharCodes(value.digits, dig, tmp);
      intpart -= tmp;
      dig += tmp;
    }

    if (intpart > 0) {
      data += stringOfChars('0', intpart);
    }
  }

  // Decimal part
  if (decpart > 0) {
    if (decimalSep !== '') {
      data += decimalSep;
    }

    tmp = Math.min(-dig, decpart);
    if (tmp > 0) {
      data += stringOfChars('0', tmp);
      decpart -= tmp;
      dig += tmp;
    }

    tmp = Math.min(digCnt - dig, decpart);

    if (tmp > 0) {
      data += stringOfCharCodes(value.digits, dig, tmp);
      decpart -= tmp;
    }

    if (decpart > 0) {
      data += stringOfChars('0', decpart);
    }
  }

  // Exponent part
  if (exppart !== '') {
    data += exppart;
  }

  return data;
}

const MAX_CURRENCY = 922337203685477.6;
const MIN_CURRENCY = -922337203685477.6;
/**
 * Converts any ValueType to a Number
 */
export function asNumberDef(
  value: ValueType,
  defaultValue: number = 0.0
): number {
  const num = tryAsNumber(
    value,
    Number.MIN_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER
  );
  return num !== null ? num : defaultValue;
}

export function asNumber(value: ValueType): number | null {
  return tryAsNumber(value, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
}

export function tryAsCurrency(value: ValueType): number | null {
  return tryAsNumber(value, MIN_CURRENCY, MAX_CURRENCY);
}

export function asCurrencyDef(
  value: ValueType,
  defaultValue: number = 0.0
): number {
  return tryAsCurrency(value) || defaultValue;
}

export function tryAsNumber(
  value: ValueType,
  minNumberValue: number,
  maxNumberValue: number
): number | null {
  if (typeof value === 'number') {
    if (value < minNumberValue || value > maxNumberValue) {
      return 0.0;
    }
    return Number(value);
  } else if (typeof value === 'string') {
    let tmp = value.replace(',', '.');
    return tmp.length > 0 ? Number.parseFloat(tmp) : null;
  } else if (typeof value === 'boolean') {
    return Number(value);
  }

  return null;
}

/**
 * Tries to convert a ValueType to an Integer.
 * If it can't, returns 'defaultValue'
 */
export const asInt = (value: ValueType, defaultValue: number = 0): number => {
  const val = tryAsInt(value);
  if (val === null) {
    return defaultValue;
  } else {
    return val;
  }
};

/**
 * Tries to convert a ValueType to an Integer. 'result'
 * in return value will be false if unsuccessful. If it is, value can not be trusted
 */
export const tryAsInt = (value: ValueType): number | null => {
  if (typeof value === 'number') {
    if (
      Number.isInteger(value) &&
      value <= Number.MAX_SAFE_INTEGER &&
      value >= Number.MIN_SAFE_INTEGER
    ) {
      return value;
    } else {
      if (value > Number.MAX_SAFE_INTEGER || value < Number.MIN_SAFE_INTEGER) {
        return null;
      } else {
        return Math.floor(value);
      }
    }
  } else if (typeof value === 'string') {
    const valAsInt = parseInt(value, 10);
    if (
      valAsInt > Number.MAX_SAFE_INTEGER ||
      valAsInt < Number.MIN_SAFE_INTEGER
    ) {
      return null;
    }
    return !Number.isNaN(valAsInt) ? valAsInt : null;
  } else if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  return null;
};

/**
 * Converts any type to a boolean, according to the same rules as in the delphi code base
 *
 * @param value The value to be converted to a boolean
 * @param defaultValue If true/false can't be determined, use this value (default = false)
 */
export function asBoolean(
  value: ValueType,
  defaultValue: boolean = false
): boolean | null {
  let valAsBool: boolean | null = null;
  switch (typeof value) {
    case 'boolean': {
      valAsBool = Boolean(value);
      break;
    }
    case 'number': {
      valAsBool = Number.isInteger(value as number) && Number(value) !== 0;
      break;
    }
    case 'string': {
      if (String(value) === '') {
        valAsBool = null;
      } else {
        valAsBool = stringToBoolean(String(value));
      }
      break;
    }
  }

  return valAsBool;
}

export function intToFixString(
  value: number,
  fixedLength: number,
  signed: boolean = false,
  fillChar: string = '0'
): string {
  if (!Number.isInteger(value)) {
    throw new Error('Floating point value passed to intToFixString');
  }

  const valueAsString = String(Math.abs(value));
  const diffLength = fixedLength - valueAsString.length;

  if (fixedLength < 1) {
    return '';
  } else if (diffLength === 0) {
    return valueAsString;
  } else if (diffLength < 0) {
    // assuming right justify
    return valueAsString.slice(2 - diffLength, 2 - diffLength + fixedLength);
  }

  let prefix = '';
  if (signed && fixedLength > 0) {
    if (value < 0) {
      prefix = '-';
    } else {
      prefix = ' ';
    }
  }
  return (
    prefix + stringOfChars(fillChar, diffLength - prefix.length) + valueAsString
  );
}

const DEFAULT_TRUE_CHAR = 'Y';
const VALID_TRUE_CHARS = [DEFAULT_TRUE_CHAR, 'Y', 'T', 'J', 'y', 't', 'j'];
const DEFAULT_FALSE_CHAR = 'N';
const VALID_FALSE_CHARS = [DEFAULT_FALSE_CHAR, 'N', 'F', 'n', 'f'];

export function stringToBoolean(
  s: string | undefined,
  defaultValue: boolean = false
): boolean {
  const result = tryStringToBoolean(s);
  if (result === null) {
    return defaultValue;
  } else {
    return result;
  }
}

export function tryStringToBoolean(s: string | undefined): boolean | null {
  let regEx = /[^A-Za-z]*([A-Za-z])/;
  const res = regEx.exec(s || '');
  if (res) {
    if (VALID_TRUE_CHARS.includes(res[1])) {
      return true;
    } else if (VALID_FALSE_CHARS.includes(res[1])) {
      return false;
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export function booleanToString(value: boolean): string {
  return value ? 'Y' : 'N';
}

export function isParseError(
  parseResult: ParseResult | PreParseResult | null | undefined
): boolean {
  return !!parseResult && parseResult > ParseResult.OK;
}

export function isParseWarning(
  parseResult: ParseResult | PreParseResult | null | undefined
): boolean {
  return !!parseResult && parseResult < ParseResult.OK;
}
